import { Component, Input, computed, signal } from '@angular/core';
import { ImageWidget, Theme } from '../../models/reader-document';

@Component({
  selector: 'wr-image-widget',
  standalone: true,
  template: `
    <div
      class="wr-image-widget px-4"
      [style.background]="background()"
      [style.border-radius.px]="cornerRadius()"
    >
      <!-- Title / Subtitle -->
      <div class="px-1">
        @if (widget()?.title || widget()?.subtitle){
        <div class="mb-2">
          @if (widget()?.title){
          <div
            [style.color]="titleColor()"
            [style.font-family]="titleFont()"
            class="title"
          >
            {{ widget()?.title }}
          </div>
          }
          @if (widget()?.subtitle){
            <div
              [style.color]="subtitleColor()"
              [style.font-family]="subtitleFont()"
              class="subtitle"
            >
              {{ widget()?.subtitle }}
            </div>
          }
        </div>
        }
      </div>

      <!-- Image -->
      <!-- TODO TAILWIND TO CSS? -->
      @if (imageSrc(); as src) {
        @if (linkUrl()) {
          <a
            [href]="linkUrl()"
            target="_blank"
            class="hover:opacity-80 duration-400 ease-in-out"
            rel="noopener noreferrer"
          >
            <img
              class="block w-full"
              [src]="src"
              [alt]="altText()"
              [style.border-radius.px]="cornerRadius()"
              (error)="onImgError($event)"
            />
          </a>
        } @else {
          <img
            class="block w-full"
            [src]="src"
            [alt]="altText()"
            [style.border-radius.px]="cornerRadius()"
            (error)="onImgError($event)"
          />
        }
      }
    </div>
  `,
})
export class ImageWidgetComponent {
  private widgetSig = signal<ImageWidget | null>(null);
  private themeSig = signal<Theme & { isDark?: boolean } | null>(null);
  private imgSrcSig = signal<string | null>(null);

  @Input() set json(value: ImageWidget) {
    this.widgetSig.set(value);
    if (value) {
      this.imgSrcSig.set(value.url || value.fallbackUrl || null);
    } else {
      this.imgSrcSig.set(null);
    }
  }

  @Input() set theme(value: Theme | undefined) {
    this.themeSig.set(value ?? null);
  }

  // basics

  widget = computed(() => this.widgetSig());
  imageSrc = computed(() => this.imgSrcSig());
  linkUrl = computed(() => this.widget()?.linkURL || null);
  altText = computed(
    () =>
      this.widget()?.altTitle ||
      this.widget()?.title ||
      this.widget()?.name ||
      this.widget()?.id ||
      ''
  );

  // theme

  private colors = computed(() => {
    const theme = this.themeSig();
    if (!theme) return null;

    return theme.isDark ? theme.darkColors : theme.lightColors;
  });

  background = computed(
    () =>
      this.widget()?.style?.['background'] ??
      'transparent'
  );

  cornerRadius = computed(
    () => this.themeSig()?.cornerRadius ?? 0,
  );

  titleColor = computed(
    () =>
      this.widget()?.style?.['titleColor'] ??
      this.colors()?.title ??
      this.colors()?.primary ??
      '#111827'
  );

  subtitleColor = computed(
    () =>
      this.widget()?.style?.['subtitleColor'] ??
      this.colors()?.subtitle ??
      this.colors()?.secondary ??
      '#4b5563'
  );

  titleFont = computed(
    () =>
      this.themeSig()?.fonts?.title?.name ??
      this.themeSig()?.fonts?.plain?.name ??
      'inherit'
  );

  subtitleFont = computed(
    () =>
      this.themeSig()?.fonts?.subtitle?.name ??
      this.themeSig()?.fonts?.plain?.name ??
      'inherit'
  );

  // error handling

  onImgError(event: Event) {
    const w = this.widgetSig();
    if (!w) return;

    if (w.fallbackUrl && this.imgSrcSig() !== w.fallbackUrl) {
      this.imgSrcSig.set(w.fallbackUrl);
      return;
    }

    // no better fallback: hide image?? or??
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
  }
}
